(() => {
  if (window.flowcoLoaded) return;
  window.flowcoLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const each = (scope, selector, fn) =>
    scope.querySelectorAll(`${selector}:not([data-fc-ready])`).forEach((el) => {
      el.dataset.fcReady = 'true';
      fn(el);
    });

  // Fade sections in as they scroll into view. Only sections below the fold are hidden.
  function initReveal(scope) {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    scope.querySelectorAll('.fc-section > .fc-wrap:not(.fc-reveal)').forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) return;
      el.classList.add('fc-reveal');
      observer.observe(el);
    });
  }

  // Hero: the highlighted word cycles through a list.
  function initRotators(scope) {
    each(scope, '[data-fc-rotator]', (el) => {
      const words = el.dataset.words.split('|').map((word) => word.trim()).filter(Boolean);
      if (reduceMotion || words.length < 2) return;
      let index = 0;
      setInterval(() => {
        el.classList.add('is-out');
        setTimeout(() => {
          index = (index + 1) % words.length;
          el.textContent = words[index];
          el.classList.remove('is-out');
        }, 260);
      }, 2200);
    });
  }

  // Hero: decorations drift a little with the pointer.
  function initParallax(scope) {
    if (reduceMotion || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    each(scope, '[data-fc-parallax]', (area) => {
      area.addEventListener('pointermove', (event) => {
        const rect = area.getBoundingClientRect();
        area.style.setProperty('--fc-px', ((event.clientX - rect.left) / rect.width - 0.5).toFixed(3));
        area.style.setProperty('--fc-py', ((event.clientY - rect.top) / rect.height - 0.5).toFixed(3));
      });
      area.addEventListener('pointerleave', () => {
        area.style.setProperty('--fc-px', 0);
        area.style.setProperty('--fc-py', 0);
      });
    });
  }

  // Flip cards: tap or click to turn over.
  function initFlips(scope) {
    each(scope, '[data-fc-flip]', (card) => {
      card.addEventListener('click', () => {
        card.setAttribute('aria-pressed', card.classList.toggle('is-flipped'));
      });
    });
  }

  // Pattern picker: tapping a swatch swaps the preview, name, price and link.
  function initPickers(scope) {
    each(scope, '[data-fc-picker]', (picker) => {
      const image = picker.querySelector('.fc-picker__stage img');
      const name = picker.querySelector('[data-fc-picker-name]');
      const price = picker.querySelector('[data-fc-picker-price]');
      const links = picker.querySelectorAll('[data-fc-picker-link]');
      const swatches = [...picker.querySelectorAll('[data-fc-swatch]')];

      const select = (swatch) => {
        swatches.forEach((item) => item.setAttribute('aria-pressed', item === swatch));
        const src = swatch.dataset.src;
        const next = new Image();
        next.onload = () => {
          image.srcset = [400, 600, 900].map((w) => `${src.replace(/width=\d+/, `width=${w}`)} ${w}w`).join(', ');
          image.src = src;
          image.alt = swatch.dataset.name;
          image.classList.remove('is-swapping');
        };
        image.classList.add('is-swapping');
        next.src = src;
        name.textContent = swatch.dataset.name;
        price.textContent = swatch.dataset.price;
        links.forEach((link) => (link.href = swatch.dataset.url));
      };

      swatches.forEach((swatch) => swatch.addEventListener('click', () => select(swatch)));
    });
  }

  // Pack finder: three questions, then pads needed between washes = pads per day x days between washes x share reusable.
  function initQuizzes(scope) {
    each(scope, '[data-fc-quiz]', (quiz) => {
      const steps = [...quiz.querySelectorAll('[data-fc-quiz-step]')];
      const dots = [...quiz.querySelectorAll('.fc-quiz__dots li')];
      const result = quiz.querySelector('[data-fc-quiz-result]');
      const picks = [...quiz.querySelectorAll('[data-fc-quiz-pick]')];
      const sizes = picks.map((pick) => Number(pick.dataset.pack)).sort((a, b) => a - b);
      let answers = [];

      const show = (index) => {
        steps.forEach((step, i) => (step.hidden = i !== index));
        result.hidden = index < steps.length;
        dots.forEach((dot, i) => dot.classList.toggle('is-done', i < index));
        if (index < steps.length) return;

        const needed = Math.max(1, Math.ceil(answers[0] * answers[1] * answers[2]));
        const size = sizes.find((pack) => pack >= needed) || sizes[sizes.length - 1];
        picks.forEach((pick) => (pick.hidden = Number(pick.dataset.pack) !== size));
        const chosen = picks.find((pick) => !pick.hidden);
        result.querySelector('[data-fc-quiz-size]').textContent = `${size} pack`;
        result.querySelector('[data-fc-quiz-why]').textContent =
          needed > size
            ? `You'd get through about ${needed} pads between washes, so start with our biggest pack and add to it.`
            : `You'd get through about ${needed} ${needed === 1 ? 'pad' : 'pads'} between washes, so this covers you.`;
        result.querySelector('[data-fc-quiz-link]').href = chosen.href;
      };

      quiz.addEventListener('click', (event) => {
        const choice = event.target.closest('[data-fc-choice]');
        if (choice) {
          answers.push(Number(choice.dataset.value));
          show(answers.length);
        } else if (event.target.closest('[data-fc-quiz-restart]')) {
          answers = [];
          show(0);
        }
      });
      show(0);
    });
  }

  // Swap calculator: pads per period x 12 periods x years.
  function initCalculators(scope) {
    each(scope, '[data-fc-calc]', (calc) => {
      const pads = calc.querySelector('[data-fc-calc-pads]');
      const years = calc.querySelector('[data-fc-calc-years]');
      const number = calc.querySelector('[data-fc-calc-number]');
      let shown = 0;
      let frame;

      const update = () => {
        calc.querySelector('[data-fc-calc-pads-out]').textContent = pads.value;
        calc.querySelector('[data-fc-calc-years-out]').textContent = `${years.value} ${years.value === '1' ? 'year' : 'years'}`;
        const target = pads.value * 12 * years.value;
        cancelAnimationFrame(frame);
        if (reduceMotion) {
          number.textContent = target.toLocaleString();
          return;
        }
        const from = shown;
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / 350, 1);
          shown = Math.round(from + (target - from) * (1 - (1 - progress) ** 3));
          number.textContent = shown.toLocaleString();
          if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      };

      pads.addEventListener('input', update);
      years.addEventListener('input', update);
      update();
    });
  }

  // Looping videos only download and play once they are near the viewport.
  function initVideos(scope) {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.play().catch(() => {});
          else entry.target.pause();
        });
      },
      { rootMargin: '200px' }
    );
    each(scope, '.fc-split__media video', (video) => {
      if (reduceMotion) {
        video.controls = true;
        return;
      }
      observer.observe(video);
    });
  }

  // Pack builder: pick a size, tap patterns to fill each pad slot. Choices go to the order as line item properties.
  function initBuilders(scope) {
    each(scope, '[data-fc-builder]', (builder) => {
      const root = builder.closest('[data-fc-product]');
      const button = root.querySelector('[name="add"]');
      const label = root.querySelector('[data-fc-add-label]');
      const addText = label.textContent.trim();
      const status = builder.querySelector('[data-fc-builder-status]');
      const hover = builder.querySelector('[data-fc-builder-hover]');
      const slots = [...builder.querySelectorAll('[data-fc-slot]')];
      const inputs = [...builder.querySelectorAll('input[type="hidden"]')];
      const patterns = [...builder.querySelectorAll('[data-fc-builder-pattern]')].filter((item) => !item.disabled);
      const picks = slots.map(() => null);
      const size = () => builder.querySelector('[data-fc-builder-size]:checked').value;

      const render = () => {
        const next = picks.indexOf(null);
        slots.forEach((slot, i) => {
          const pick = picks[i];
          slot.classList.toggle('is-filled', !!pick);
          slot.classList.toggle('is-next', i === next);
          slot.replaceChildren();
          if (pick) {
            const image = document.createElement('img');
            image.src = pick.image;
            image.alt = '';
            const badge = document.createElement('span');
            badge.className = 'fc-slot__size';
            badge.textContent = pick.size.charAt(0);
            slot.append(image, badge);
            slot.setAttribute('aria-label', `Pad ${i + 1}: ${pick.pattern}, ${pick.size}. Tap to remove.`);
          } else {
            const number = document.createElement('span');
            number.className = 'fc-slot__number';
            number.textContent = i + 1;
            slot.append(number);
            slot.setAttribute('aria-label', `Pad ${i + 1}: empty`);
          }
          inputs[i].value = pick ? `${pick.pattern} / ${pick.size}` : '';
          inputs[i].disabled = !pick;
        });
        const chosen = picks.filter(Boolean).length;
        const left = picks.length - chosen;
        status.textContent = `${chosen} of ${picks.length} chosen`;
        status.classList.toggle('is-full', left === 0);
        button.disabled = left > 0;
        label.textContent = left > 0 ? `Choose ${left} more ${left === 1 ? 'pad' : 'pads'}` : addText;
        root.dispatchEvent(new Event('change'));
      };

      const add = (swatch) => {
        const index = picks.indexOf(null);
        if (index === -1) return;
        picks[index] = { pattern: swatch.dataset.fcBuilderPattern, size: size(), image: swatch.querySelector('img').currentSrc || swatch.querySelector('img').src };
        render();
      };

      patterns.forEach((swatch) => {
        swatch.addEventListener('click', () => add(swatch));
        swatch.addEventListener('pointerenter', () => (hover.textContent = swatch.dataset.fcBuilderPattern));
        swatch.addEventListener('pointerleave', () => (hover.textContent = ''));
      });
      slots.forEach((slot, i) =>
        slot.addEventListener('click', () => {
          picks[i] = null;
          render();
        })
      );
      builder.querySelector('[data-fc-builder-random]').addEventListener('click', () => {
        picks.forEach((pick, i) => {
          if (pick) return;
          const swatch = patterns[Math.floor(Math.random() * patterns.length)];
          picks[i] = { pattern: swatch.dataset.fcBuilderPattern, size: size(), image: swatch.querySelector('img').currentSrc || swatch.querySelector('img').src };
        });
        render();
      });
      builder.querySelector('[data-fc-builder-clear]').addEventListener('click', () => {
        picks.fill(null);
        render();
      });
      render();
    });
  }

  // Product page: hearts pop out of the add-to-cart button, and a sticky bar appears once it scrolls away.
  function initProducts(scope) {
    each(scope, '[data-fc-product] product-form', (wrapper) => {
      const root = wrapper.closest('[data-fc-product]');
      const button = wrapper.querySelector('[name="add"]');
      const sticky = root.querySelector('[data-fc-sticky]');

      wrapper.querySelector('form').addEventListener('submit', () => {
        if (reduceMotion || button.disabled) return;
        const source = sticky && sticky.classList.contains('is-visible') ? sticky.querySelector('button') : button;
        const rect = source.getBoundingClientRect();
        for (let i = 0; i < 12; i += 1) {
          const heart = document.createElement('span');
          heart.className = 'fc fc-heart';
          heart.style.left = `${rect.left + rect.width * Math.random()}px`;
          heart.style.top = `${rect.top + rect.height / 2}px`;
          heart.style.setProperty('--fc-dx', `${(Math.random() - 0.5) * 220}px`);
          heart.style.setProperty('--fc-dy', `${-90 - Math.random() * 150}px`);
          heart.style.setProperty('--fc-rot', `${(Math.random() - 0.5) * 120}deg`);
          heart.style.animationDelay = `${Math.random() * 0.15}s`;
          document.body.append(heart);
          heart.addEventListener('animationend', () => heart.remove());
        }
      });

      if (!sticky) return;
      const price = root.querySelector('[data-fc-price-now]');
      const stickyPrice = sticky.querySelector('[data-fc-sticky-price]');
      const stickyButton = sticky.querySelector('button');
      const sync = () => {
        stickyPrice.textContent = price.textContent;
        stickyButton.disabled = button.disabled;
      };
      // Show the bar once the main button has scrolled above the viewport.
      let queued = false;
      const place = () => {
        queued = false;
        const passed = button.getBoundingClientRect().bottom < 0;
        if (passed && !sticky.classList.contains('is-visible')) sync();
        sticky.classList.toggle('is-visible', passed);
      };
      window.addEventListener(
        'scroll',
        () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(place);
        },
        { passive: true }
      );
      root.addEventListener('change', () => setTimeout(sync));
      stickyButton.addEventListener('click', () => button.click());
      sticky.hidden = false;
      place();
    });
  }

  const init = (scope = document) => {
    initReveal(scope);
    initRotators(scope);
    initParallax(scope);
    initFlips(scope);
    initPickers(scope);
    initQuizzes(scope);
    initCalculators(scope);
    initVideos(scope);
    initBuilders(scope);
    initProducts(scope);
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
